import React, { useState, useCallback } from 'react'
import cls from '@UI/crypto_exchange/desktop/components/modals/ProblemGamblerModal/ProblemGamblerModal.module.scss'
import { useAppDispatch } from '@App/hooks'
import { asyncLogout } from '@Redux/slices/user/UserSlice'
import { setModal } from '@Redux/slices/modals/ModalSlice'
import { MODAL_TYPES } from '@Redux/slices/modals/enums'
import { IDocument } from '@Interfaces/CommonInterfaces'
import {
  useCheckUserAnswersMutation,
  useGetDocumentRequestsQuery
} from '@Redux/api/user.api'

interface Props {
  closeModal: () => void
  doc: IDocument
  changeStep: (step: number, params?: any) => void
}

const Step2: React.FC<Props> = ({ doc, changeStep, closeModal }) => {
  const dispatch = useAppDispatch()
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [showedQuestion, setShowedQuestion] = useState(0)
  const [disabledContinue, setDisabledContinue] = useState(true)
  const [disabledButtons, setDisabledButtons] = useState(false)

  const [checkAnswersMutation, { isLoading }] = useCheckUserAnswersMutation()
  const { refetch: refetchDocumentRequests } = useGetDocumentRequestsQuery()

  const questions = doc?.questions || []
  const questionCount = questions.length
  const currentQuestion = questions[showedQuestion]

  const onGetResponse = useCallback(
    async (response: any) => {
      const type = response?.parameters?.type || response?.data?.type

      switch (type) {
        case 'ok':
          changeStep(4)
          await refetchDocumentRequests()
          break
        case 'error_timeout':
          changeStep(3)
          await refetchDocumentRequests()
          break
        case 'error_close':
          closeModal()
          dispatch(asyncLogout())
          dispatch(setModal({ modalType: MODAL_TYPES.CLOSE_ACCOUNT_MODAL }))
          break
        case 'error_exclude':
          const excludeParams = response?.parameters?.params || response?.data?.params || []
          changeStep(6, excludeParams)
          break
        default:
          console.error('Unknown response type:', type)
          changeStep(4)
      }
    },
    [changeStep, closeModal, dispatch, refetchDocumentRequests],
  )

  const answerQuestion = (key: string, answer: number) => {
    setAnswers((prev) => ({
      ...prev,
      [key]: answer,
    }))
    setDisabledContinue(false)
  }

  const changeQuestion = (newQuestionIndex: number) => {
    if (questionCount === newQuestionIndex) {
      setDisabledButtons(true)
      setDisabledContinue(true)
    } else {
      setShowedQuestion(newQuestionIndex)
      setDisabledContinue(true)
    }
  }

  const checkAnswers = async () => {
    const nextQuestionIndex = showedQuestion + 1

    if (questionCount !== nextQuestionIndex) {
      changeQuestion(nextQuestionIndex)
    } else {
      setDisabledContinue(true)
      setDisabledButtons(true)

      try {
        const requestData = {
          answers: answers,
          request_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }

        const response = await checkAnswersMutation(requestData).unwrap()

        await onGetResponse(response)
      } catch (error: any) {
        console.error('Failed to check answers:', error)

        if (error?.message) {
          console.error('Error message:', error.message)
        }
      }
    }
  }

  if (!questionCount) {
    return null
  }

  const oneStepWidth = 100 / (questionCount + 1)
  const filledLineWidth = oneStepWidth * (showedQuestion + 1)

  const yesButtonClass = currentQuestion.key in answers && answers[currentQuestion.key] === 1 ? ' ' + cls.active : ''
  const noButtonClass = currentQuestion.key in answers && answers[currentQuestion.key] === 0 ? ' ' + cls.active : ''

  return (
    <div className={`${cls.step} ${cls.second}`}>
      <div className={cls.stepLine}>
        <i className={cls.stepLineFill} style={{ width: `${filledLineWidth}%` }} />
      </div>

      <div className={cls.stepTitle}>
        Question {showedQuestion + 1}/{questionCount}
      </div>

      <div className={cls.title}>{currentQuestion.title}</div>

      <div className={`${cls.description} ${cls.centerAlign}`}>{currentQuestion.description}</div>

      <div className={cls.buttons}>
        <button
          className={`${cls.button} ${cls.answerButton}${noButtonClass}`}
          disabled={disabledButtons || isLoading}
          onClick={() => answerQuestion(currentQuestion.key, 0)}
        >
          <b>No</b>, does not apply to me
        </button>
        <button
          className={`${cls.button} ${cls.answerButton}${yesButtonClass}`}
          disabled={disabledButtons || isLoading}
          onClick={() => answerQuestion(currentQuestion.key, 1)}
        >
          <b>Yes</b>, applies to me
        </button>
      </div>

      <button
        className={`${cls.button} ${cls.centerBtn} ${cls.primaryButton}`}
        disabled={disabledContinue || isLoading}
        onClick={checkAnswers}
      >
        {isLoading ? 'Processing...' : 'Continue'}
      </button>
    </div>
  )
}

export default Step2
